/**
 * Analytics Hook
 * Provides easy access to analytics tracking with auto-tracking features
 *
 * READS `next/router` DIRECTLY, NOT `useRouterContext`. That is the whole point
 * of this file's shape, so do not "tidy" it back onto the context.
 *
 * `pages/_app.tsx` calls this from `AppContent`, which RENDERS
 * `CompositeAppProvider` in its own return -- and `RouterProvider` lives inside
 * that provider. So a `useRouterContext()` call here sits ABOVE its own
 * provider and silently takes the no-provider fallback in
 * `contexts/RouterProvider.tsx`, which returns a hardcoded `asPath: '/'`. The
 * effect's only dependency then never changes for the life of the page, so
 * `pageView()` fired once per hard load and never again.
 *
 * It was invisible because nothing throws: the fallback is deliberate, and the
 * unit test mocked `useRouterContext` to return a path that changed, which is
 * the one thing production could not do. Measured on production 2026-09-18,
 * over the two days after the identity rollout: of 11,612 sessions that
 * recorded a page view, 11,597 had exactly ONE distinct `page_url`, 15 had two,
 * and none had three. On a marketplace where every listing is reached by a
 * client-side navigation, that is not a traffic pattern -- it is the whole of
 * SPA navigation missing from `events`.
 *
 * `useRouter()` has no such dependency: it is Next's own hook, works at any
 * depth including directly under `_app`, and its `asPath` updates on every
 * client-side navigation.
 */

import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { analytics } from '../utils/analytics';
import { scoreSessionOnce } from '../utils/session-bot-score';
import { emailClickFromUrl } from '../utils/email-click';

/**
 * Record the email click this landing carries, once.
 *
 * A reload of the same tagged URL is not a second click, so the URL is
 * remembered for the tab. Storage can throw (private mode, blocked site data);
 * then the click is still recorded, at worst twice on a reload.
 */
function recordEmailClickOnce(href: string): void {
  const click = emailClickFromUrl(href);
  if (!click) return;

  const key = `sw_email_click:${href}`;
  try {
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
  } catch {
    // Fall through and record it.
  }
  analytics?.emailClick(click);
}

export function useAnalytics() {
  const { asPath } = useRouter();
  const previousPath = useRef(asPath);

  // An email link lands as a hard load, so this runs once, on arrival.
  // Effects run only in the browser, so `window` is always there.
  useEffect(() => {
    recordEmailClickOnce(window.location.href);
  }, []);

  // Track page views on route change
  useEffect(() => {
    // Fires on every `asPath` change, back-navigation included: returning to a
    // page the visitor has already seen is a page view, and the funnel work
    // counts arrivals, not distinct URLs.
    analytics?.pageView();

    // One reCAPTCHA score per session, joined to every row by session_id. A
    // no-op after the first success; retried here if the script was not ready.
    void scoreSessionOnce();

    previousPath.current = asPath;
  }, [asPath]);

  return analytics;
}
